// /src/hooks/useGroupGate.js
// The §4 routing gate's membership + published-landing resolve (Groups Tier 1), lifted out of
// MainFeed.jsx unchanged except for one rule, so it can be tested: every re-check starts from
// not-a-member (Roadmap #100, docs/briefs/2026-10-16-sign-out-clears-the-group-addendum-1.md
// ruling 2). A membership answer belongs to one viewer and one group; while the next answer is
// loading, the private interior must not render on the previous one. Before this, a re-check kept
// the previous isMember, so signing out (or another account signing in) on a private group left
// its interior on screen until the check returned.
//
// The community_landing read is carried over verbatim from MainFeed (a pre-existing direct fetch
// outside /src/data/ — see the sign-out ledger).

import { useEffect, useState } from 'react';
import { groupService } from '../data/dataService';
import { supabaseFetch } from '../lib/supabase';

// The state every check starts from, and the state outside a group. isMember starts false on
// every (re)check, so the interior never renders before the viewer's own answer arrives.
export const GATE_CHECKING = Object.freeze({ loading: true, isMember: false, hasLanding: false });
const GATE_NONE = Object.freeze({ loading: false, isMember: false, hasLanding: false });

/**
 * @returns {{ groupGate: {loading:boolean, isMember:boolean, hasLanding:boolean},
 *   setGateRefresh: Function }} setGateRefresh re-runs the resolve (e.g. after a join from the gate)
 */
export function useGroupGate({ currentContext, selectedGroup, isLoggedIn, currentUser }) {
  const [groupGate, setGroupGate] = useState(GATE_CHECKING);
  const [gateRefresh, setGateRefresh] = useState(0);

  useEffect(() => {
    if (currentContext !== 'groups' || !selectedGroup?.id) {
      setGroupGate(GATE_NONE);
      return undefined;
    }
    let cancelled = false;
    setGroupGate(GATE_CHECKING);
    (async () => {
      // Membership (owner counts as member).
      let isMember = false;
      if (isLoggedIn && currentUser?.id) {
        if (selectedGroup.owner_id === currentUser.id) {
          isMember = true;
        } else {
          try {
            const m = await groupService.checkMembership(currentUser.id, selectedGroup.id);
            isMember = !!m?.isMember;
          } catch (e) { console.error('Gate membership check failed:', e); }
        }
      }
      // Published landing — only matters for a private non-member (landing vs minimal gate).
      let hasLanding = false;
      if (selectedGroup.is_private && !isMember) {
        try {
          const { data } = await supabaseFetch.query('community_landing', {
            select: 'is_published',
            eq: { community_id: selectedGroup.id },
            single: true
          });
          hasLanding = !!data?.is_published;
        } catch (e) { /* no landing row → minimal gate */ }
      }
      if (!cancelled) setGroupGate({ loading: false, isMember, hasLanding });
    })();
    return () => { cancelled = true; };
  }, [currentContext, selectedGroup?.id, selectedGroup?.is_private, selectedGroup?.owner_id, isLoggedIn, currentUser?.id, gateRefresh]);

  return { groupGate, setGateRefresh };
}
